import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    createT2DRHealthDataEntry,
    updateT2DRHealthDataEntry
} from '../../../../store/participantService';
import { formatDate } from '../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import StaticField from '../../../formElements/StaticField';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateT2DRHealthData';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const EditT2DRHealthData = ({
    participant,
    onClose,
    isDisabled,
    attendedModules,
    linkedModuleId,
    formType,
    row = {},
    firstEntry
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant T2DR Health Data entry has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        participantId: '',
        tdrConsumptionId: '',
        rescuePackageOfferedId: '',
        rescuePackageDeclinedReasonId: '',
        rescuePackageTypeId: '',
        rescuePackageMedicationConfirmationId: '',
        tdrDaysMissed: '',
        previouslySuppliedFibreSupplementsId: '',
        actualTdrStartDate: '', // only set on first entry added - then default all other rows to that date
        linkedModuleId: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [modules, setModules] = useState([]);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [canEditDate, setCanEditDate] = useState(false);

    // STORE STATE
    const tdrConsumptionDetails = useSelector(
        (state) => state.entities.directusService.tdrConsumptionDetails
    );
    const previouslySuppliedFibreSupplements = useSelector(
        (state) => state.entities.directusService.previouslySuppliedFibreSupplements
    );
    const rescuePackageOfferedDetails = useSelector(
        (state) => state.entities.directusService.rescuePackageOfferedDetails
    );
    const rescuePackageTypes = useSelector(
        (state) => state.entities.directusService.rescuePackageTypes
    );
    const rescuePackageDeclinedReasonTypes = useSelector(
        (state) => state.entities.directusService.rescuePackageDeclinedReasonTypes
    );
    const rescuePackageMedicationConfirmationDetails = useSelector(
        (state) => state.entities.directusService.rescuePackageMedicationConfirmationDetails
    );

    // USEEFFECTS
    useEffect(() => {
        let updated = { ...newEntry };
        if (formType === 'create') {
            updated = {
                ...updated,
                actualTdrStartDate: firstEntry?.actualTdrStartDate || '',
                linkedModuleId: linkedModuleId || ''
            };
        } else if (formType === 'edit') {
            updated = {
                ...row,
                tdrDaysMissed: row.tdrDaysMissed || ''
            };
        }
        setNewEntry({ ...updated });
        setInitialRow({ ...updated });

        setCanEditDate(
            (formType === 'create' && !('id' in firstEntry)) ||
                (formType === 'edit' && firstEntry?.id === row.id)
        );
    }, [formType, row, firstEntry]);

    useEffect(() => {
        let modules = [...attendedModules];
        if (formType === 'edit') {
            if (
                row.linkedModuleId &&
                row.linkedModule &&
                !modules.find((el) => el.id === row.linkedModuleId)
            ) {
                modules = [...modules, { id: row.linkedModuleId, name: row.linkedModule }];
            }
        }
        setModules(modules);
    }, [attendedModules]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onTDRDaysMissedChange = (e) => {
        onUpdate('tdrDaysMissed', e.target.value.replace(regExps.DIGITS_R, ''));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createT2DRHealthDataEntry(payload, _MESSAGE))
            : dispatch(updateT2DRHealthDataEntry(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form
                className={form.form}
                onSubmit={onSubmit}
                data-testid="edit_t2dr_health_data_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add New T2DR Health Data' : 'Edit T2DR Health Data'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="tdrConsumptionId"
                            label="TDR Consumption"
                            placeholder="Select TDR consumption..."
                            disabled={isDisabled}
                            menuItems={tdrConsumptionDetails || []}
                            selectedId={newEntry.tdrConsumptionId}
                            selected={getObjFromId(
                                tdrConsumptionDetails,
                                newEntry.tdrConsumptionId
                            )}
                            onChange={(chosenId) => onUpdate('tdrConsumptionId', chosenId)}
                        />
                        <SingleSelect
                            id="previouslySuppliedFibreSupplementsId"
                            label="Previously Supplied Fibre Supplements"
                            placeholder="Select previously supplied fibre supplements..."
                            disabled={isDisabled}
                            menuItems={previouslySuppliedFibreSupplements || []}
                            selectedId={newEntry.previouslySuppliedFibreSupplementsId}
                            selected={getObjFromId(
                                previouslySuppliedFibreSupplements,
                                newEntry.previouslySuppliedFibreSupplementsId
                            )}
                            onChange={(chosenId) =>
                                onUpdate('previouslySuppliedFibreSupplementsId', chosenId)
                            }
                        />
                        <TextInputField
                            id="tdrDaysMissed"
                            label="TDR Days Missed"
                            placeholder="Enter TDR days missed..."
                            disabled={isDisabled}
                            maxLength={3}
                            value={newEntry.tdrDaysMissed}
                            error={errors.tdrDaysMissed}
                            onChange={onTDRDaysMissedChange}
                        />
                        {canEditDate ? (
                            <DateSelect
                                value={newEntry.actualTdrStartDate}
                                label="Actual TDR Start Date"
                                disabled={isDisabled}
                                error={errors.actualTdrStartDate}
                                onDateChange={(date) => onUpdate('actualTdrStartDate', date)}
                            />
                        ) : (
                            <StaticField
                                id="actualTdrStartDate"
                                label="Actual TDR Start Date"
                                content={formatDate(newEntry.actualTdrStartDate) || ' - '}
                                customClass="dateInfo"
                            />
                        )}

                        <SingleSelect
                            id="linkedModuleId"
                            label="Linked Module"
                            placeholder="Select linked module..."
                            disabled={isDisabled}
                            menuItems={modules || []}
                            selectedId={newEntry.linkedModuleId}
                            selected={getObjFromId(modules, newEntry.linkedModuleId)}
                            onChange={(chosenId) => onUpdate('linkedModuleId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="rescuePackageOfferedId"
                            label="Rescue Package Offered"
                            placeholder="Select rescue package offered..."
                            disabled={isDisabled}
                            menuItems={rescuePackageOfferedDetails || []}
                            selectedId={newEntry.rescuePackageOfferedId}
                            selected={getObjFromId(
                                rescuePackageOfferedDetails,
                                newEntry.rescuePackageOfferedId
                            )}
                            onChange={(chosenId) => onUpdate('rescuePackageOfferedId', chosenId)}
                        />
                        <SingleSelect
                            id="rescuePackageDeclinedReasonId"
                            label="Rescue Package Declined Reason"
                            placeholder="Select rescue package declined reason..."
                            disabled={isDisabled}
                            menuItems={rescuePackageDeclinedReasonTypes || []}
                            selectedId={newEntry.rescuePackageDeclinedReasonId}
                            selected={getObjFromId(
                                rescuePackageDeclinedReasonTypes,
                                newEntry.rescuePackageDeclinedReasonId
                            )}
                            onChange={(chosenId) =>
                                onUpdate('rescuePackageDeclinedReasonId', chosenId)
                            }
                        />
                        <SingleSelect
                            id="rescuePackageTypeId"
                            label="Rescue Package Type"
                            placeholder="Select rescue package type..."
                            disabled={isDisabled}
                            menuItems={rescuePackageTypes || []}
                            selectedId={newEntry.rescuePackageTypeId}
                            selected={getObjFromId(
                                rescuePackageTypes,
                                newEntry.rescuePackageTypeId
                            )}
                            onChange={(chosenId) => onUpdate('rescuePackageTypeId', chosenId)}
                        />
                        <SingleSelect
                            id="rescuePackageMedicationConfirmationId"
                            label="Rescue Package Medication Confirmation"
                            placeholder="Select rescue package medication confirmation..."
                            disabled={isDisabled}
                            menuItems={rescuePackageMedicationConfirmationDetails || []}
                            selectedId={newEntry.rescuePackageMedicationConfirmationId}
                            selected={getObjFromId(
                                rescuePackageMedicationConfirmationDetails,
                                newEntry.rescuePackageMedicationConfirmationId
                            )}
                            onChange={(chosenId) =>
                                onUpdate('rescuePackageMedicationConfirmationId', chosenId)
                            }
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditT2DRHealthData.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object,
    firstEntry: PropTypes.object,
    attendedModules: PropTypes.arrayOf(PropTypes.object),
    linkedModuleId: PropTypes.string
};

export default EditT2DRHealthData;
